'use client'

// The town itself — every static and ambient thing that makes the place
// the place, shared by the live scene AND the director's stage. This
// exists because the director used to hand-copy this list and drift: a
// model added to the world (the octopus, the spray cans, whatever lands
// next) silently never appeared on the director's set. One list, two
// mounts, no drift. Anything wired to a RUNNING sim (entity markers, food,
// signals, the NPC bridge) stays out of here and lives with its scene.

import { Suspense } from 'react'
import { SHRINES, WORLD } from '../../../lib/world/sceneConfig'
import TestMap from '../testMap'
import { Cat } from '../cat'
import { Sky } from '../sky2'
import { Buildings } from './Buildings'
import { Ruins } from './Ruins'
import { SprayPaint } from './SprayPaint'
import { AdBoards } from './AdBoards'
import { ArtWall } from './ArtWall'
import { RadioTower } from './RadioTower'
import { Phone } from './Phone'
import { Fences } from './Fences'
import { Lake } from './Lake'
import { PondArea } from './PondArea'
import { GreenStone } from './GreenStone'
import { MoodRig } from '../environment/MoodRig'
import { Ambience } from '../environment/Ambience'
import { Rain } from '../environment/Rain'
import { McDonalds } from '../models/McDonalds'
import { Starbucks } from '../models/Starbucks'
import { Containers } from '../models/Containers'
import { Casino } from '../models/Casino'
import { Speakers } from '../models/Speakers'
import { Mushrooms } from '../models/Mushrooms'
import { Meadow } from './Meadow'
import { PianoAudio } from './PianoAudio'
import { RackAudio } from './RackAudio'
import { Piano } from './Piano'
import { JunkHeap } from './JunkHeap'
import { TacoTruck } from './TacoTruck'
import { JobCentre } from './JobCentre'
import { Laundrette } from './Laundrette'
import { Carriage } from './Carriage'
import { ClockTower } from './ClockTower'
import { SprayWalls } from './SprayWalls'
import { TagWalls } from './TagWalls'
import { PrintShelf } from './PrintShelf'
import { Greenhouse } from './Greenhouse'
import { TornMap } from './TornMap'
import { Wants } from './Wants'
import { Halloween } from './Halloween'
import { Hoard } from './Hoard'
import { Rack } from './Rack'
import { Monorail } from './Monorail'

// The meadow is in the world now. The trial gate is gone rather than
// flipped to a constant, because the sim has its own meadowEnabled and two
// switches for one place is how you end up with him lying down in a field
// that is not being drawn.
import { InternetCafe } from '../models/InternetCafe'
import { StreetLights } from '../models/StreetLights'
import { Wires } from '../models/Wires'
import { Rubbish } from '../models/Rubbish'
import HeightFog from '../effects/HeightFog'
import Wind from '../effects/wind/Wind'
import Debris from '../effects/wind/Debris'
import TreesTSL from '../trees'

export function TownDressing() {
  return (
    <>
      <TestMap size={WORLD.floorSize} />

      {/* Spray cans in the rubbish by the dumpsters, and the art wall down
          the path from the studio. Both visible before he ever draws:
          paint in a skip and an empty wall is the town setting its own
          stage. */}
      <SprayPaint />
      <ArtWall />
      <AdBoards />

      {/* THE REAL TOWN — buildings.glb, positions/rotations baked in
          Blender (drops in at identity). Draco + WebP, ~1.4MB. */}
      <Buildings />

      {/* Ruins — standalone baked model, untextured for now. */}
      <Ruins />

      {/* Radio tower — Sketchfab mast, KTX2 base+normal, uncompressed geo
          (clean UVs/tangents); placed via <group> to match the
          main-checkout placement. */}
      <RadioTower />

      {/* Payphone — phone.glb, placement baked at the phone POI. */}
      <Phone />

      {/* Fences — 29 wire panels, GPU-instanced; wire = alpha-mask cutout.
          Positions baked into the instances. */}
      <Fences />

      {/* Lake — stylised TSL water (depth foam + ripples). Links to
          activity_pond. */}
      <Lake />

      {/* Jetty, rocks and a stand of reeds on the lake's north-west
          shore. Scenery only: no entity, no mechanics. */}
      <PondArea />

      {/* The stone he asked the octopus for. The light it gives off is
          the sim's (an ARTIFACT with green resonance), not this file's. */}
      <GreenStone />

      {/* WEATHER = MOOD: MoodRig smooths the live environment into shared
          TSL uniforms (night, wetness, overcast, danger…); Rain + Lights +
          sky2 all read them. Night = blue moon directional only (no point
          lights, ground pools, or bulbs). */}
      <MoodRig />
      <Ambience />
      <Rain />

      {/* Static visual models — positioned freely; sim entity coords are
          separate. */}
      <McDonalds />
      <Starbucks />
      {/* All four nest clusters are baked into containers_mat.glb at their
          world positions, so one mount paints every nest. The sim spawns a
          NEST entity at each rest POI, inside the open container of each
          since 30 Oct. */}
      <Containers />
      {/* The fifth roost, a tube carriage he goes inside to sleep, world
          coords baked, identity. */}
      <Carriage />
      <Casino />
      <Speakers />
      {/* The oversized caps from his wish, baked at their world positions,
          colour in the vertices. The eatMushroom_N POIs sit at their feet. */}
      <Mushrooms />
      <SprayWalls />
      {/* the walls that stand on their own for him to paint (the rave's), world coords baked */}
      <TagWalls />
      <Meadow />
      <Piano />
      <PianoAudio />
      <RackAudio />
      {/* The junk heap behind the east shops, laid out in Blender at
          world coordinates, so it renders at identity. */}
      <JunkHeap />
      {/* The taco truck by the soup cart, world coords baked, identity. */}
      <TacoTruck />
      {/* The job centre's front over buildings_25's shutters, once it is open. World coords baked, identity. */}
      <JobCentre />
      {/* The laundrette on the north side of town past the monorail, the
          Laundry pack's building with its machines. World coords baked,
          identity. */}
      <Laundrette />
      {/* The town clock at the end of the east street, world coords
          baked, so identity again. */}
      <ClockTower />
      <InternetCafe />
      {/* What has come off the workshop printer, newest few, capped by
          construction (see PrintShelf). Empty until he prints. */}
      <PrintShelf />
      {/* The greenhouse by the meadow, placeholder until the model lands.
          The sim's beds and their clocks are in Garden.js. */}
      <Greenhouse />
      <TornMap />
      <Hoard />
      <Rack />
      {/* The things he asked for at the shrine, each drawn once its ask is
          granted (wants.glb, authored at world coordinates). */}
      <Wants />
      {/* The Halloween weekend: pumpkins, graves, spiders and webs, drawn only
          while the town says it is on (world/Halloween.js) and gone after
          Sunday by themselves. */}
      <Halloween />
      {/* Cat shrine (= shiny entity for curiosity), on its point at the middle
          of the square and inside its collider (Cube.358 in obstacles.glb,
          built round it there: SHRINES in lib/world/sceneConfig.js). It faces
          +z, down the square. (it stood 5.5 back, in the ruins' steps, after
          the shrine point lost its sign in July and was nudged 2.9 forward to
          sit half in its box, 17 Aug) */}
      <group scale={6} position={[SHRINES.center[0], SHRINES.center[1] - 0.6, SHRINES.center[2]]}>
        <Cat />
      </group>

      <Sky />
      <HeightFog density={0.004} />
      <Wind />
      <Debris />
      <TreesTSL />
      <StreetLights />
      <Wires />
      <Rubbish />
      {/* The monorail's beam and pylons along the curve in the blend's
          'Monorail' collection (scripts/blender/monorail-sync.py). Its own
          Suspense, so its texture can never hold up the town. */}
      <Suspense fallback={null}>
        <Monorail />
      </Suspense>
    </>
  )
}

export default TownDressing
